import React, { Suspense, useEffect } from "react";
//Keep it first so data dog, etc can init before the rest of the app.
import "monitoring/BrowserLogs";
import {
  Provider as ReduxProvider,
  useDispatch,
  useSelector
} from "react-redux";
import { Router } from "polyrouter";
import { DefaultEnv } from "store/env";
import { Store } from "redux";
import Routes from "routes/Routes";
import configureStore, { State } from "store";
import { FlagProvider } from "FlagProvider";
import { LocationTracker, Rum } from "monitoring";
import { PermissionsProvider as RolesProvider } from "rbac";
import { Notifications } from "collections/Notifications";
import { createBrowserHistory, History } from "history";
import "./styles/app.scss";
import { LoadingIndicator, ErrorPage } from "./elements";
import { AppErrorBoundaryRaw } from "./routes/AppErrorBoundary";
import { AuthProvider } from "./auth";
import { RouteConfirmation } from "./collections/Form/BlockingForm";
import { loadUserDataFromLocalStorage } from "store/userMeta";
import { loadImportDataFromLocalStorage } from "store/import";

type AppProps = {
  store: Store;
  history: History;
};

export const AppWithReduxEnv: React.FC<{ history: History }> = ({
  history
}) => {
  const useMock = useSelector((state: State) => state.env.useMock);
  let PolyGraphQl = useMock
    ? React.lazy(() => import("polygraphql/mock"))
    : React.lazy(() => import("polygraphql"));

  const Auth = useMock ? React.Fragment : AuthProvider;
  const authProps: any = useMock ? {} : { history };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(loadUserDataFromLocalStorage());
    dispatch(loadImportDataFromLocalStorage());
  }, [dispatch]);

  return (
    <Suspense
      fallback={
        <LoadingIndicator
          className="theme-independent"
          title="Initializing Auth Connection"
        />
      }
    >
      <Router history={history}>
        <Auth {...authProps}>
          <LocationTracker />
          <Rum />
          <Suspense
            fallback={
              <LoadingIndicator
                className="theme-independent"
                title="Initializing Backend Connection"
              />
            }
          >
            <FlagProvider>
              <PolyGraphQl>
                <RolesProvider>
                  <Routes />
                </RolesProvider>
              </PolyGraphQl>
            </FlagProvider>
          </Suspense>
          <Notifications />
        </Auth>
      </Router>
    </Suspense>
  );
};

export const App: React.FC<AppProps> = ({ store, history }) => {
  //To run with a mock graphql run like `REACT_APP_GRAPHQL_USE_MOCK='true' yarn start`

  return (
    <ReduxProvider store={store}>
      {/*Redux must always be the first initialized, other providers may depend on its values. */}
      <AppWithReduxEnv history={history} />
    </ReduxProvider>
  );
};

let DefaultApp = () => {
  const history = createBrowserHistory({
    basename: DefaultEnv.rootUrl,
    getUserConfirmation: (message, callback) =>
      RouteConfirmation(message, callback)
  });
  const AppStore = configureStore();
  return (
    <AppErrorBoundaryRaw
      errorComponent={
        <div className={`theme-independent`}>
          <ErrorPage />
        </div>
      }
    >
      <App store={AppStore} history={history} />
    </AppErrorBoundaryRaw>
  );
};
export default DefaultApp;
